/** Transcription (VOICE-ASR, SDD §20.2).
 *
 *  The seam between the audio this build really captures and the words it does not really have. `capture.ts` hears
 *  that somebody is speaking — that is arithmetic on samples and needs nothing. Turning it into text is a streaming
 *  ASR call, which needs a provider credential, and this build has none.
 *
 *  So there is a port with two bindings and only one of them exists. The simulated one returns a scripted utterance
 *  and says `simulated: true` on every result, which the HUD carries through to the badge. It is deterministic — the
 *  same sequence every session — because a made-up transcript that varied would look like recognition.
 *
 *  The live binding is the missing half. It is written here as a type rather than a stub so that "the ASR is not
 *  built" is a fact about this file rather than something you discover when a stub returns an empty string.
 */

export interface TranscriptionResult {
  readonly text: string;
  /** Carried to the HUD badge. A transcript that is not really a transcript has to say so at the point of use. */
  readonly simulated: boolean;
}

export interface Transcriber {
  /** The utterance so far, for the dimmed partial line the HUD shows while listening. A live streaming ASR emits
   *  these as it recognises; the simulated one knows the whole sentence in advance and returns it. */
  partial(): string;
  /** Called at the endpoint with the utterance that was captured. */
  finish(): Promise<TranscriptionResult>;
  reset(): void;
}

/** Scripted utterances, in order, looping. Each one exercises a different skill so that pressing the mic repeatedly
 *  walks the router rather than replaying one answer. */
export const SIMULATED_UTTERANCES: readonly string[] = [
  'What is my expectancy on London opens?',
  'Did I follow my rules today?',
  'Journal that the Asia fade was off-plan',
  'Arm the session for tomorrow',
  'How do I set a drawdown circuit breaker?',
];

export function createSimTranscriber(): Transcriber {
  let index = 0;
  const current = () => SIMULATED_UTTERANCES[index % SIMULATED_UTTERANCES.length] ?? SIMULATED_UTTERANCES[0]!;
  return {
    partial: current,
    async finish() {
      const text = current();
      index += 1;
      return { text, simulated: true };
    },
    reset() {
      index = 0;
    },
  };
}
